/**
 * 标准配合剩余自由度解析。
 *
 * 文件职责：
 * 1. 从 StandardMateSolverInput 的配合图中解析组件仍可被交互驱动的自由度；
 * 2. 为移动/旋转命令提供 plain-data 结果，避免命令层自行理解 Mate 几何语义；
 * 3. 保持与求解器相同的 world-space 几何变换规则，避免预览、拖动和提交语义漂移。
 */

import * as THREE from 'three';
import { AssemblyComponentFixationState } from '../component-fixation';
import {
  AssemblyMateType,
  MateSolveStatus,
  type AssemblyMateDefinition,
} from '../definition';
import {
  normalizeVector,
  resolveAxisGeometry,
  resolvePlaneGeometry,
  tupleFromVector,
  type AxisGeometry,
} from './geometry/StandardMateGeometryAccess';
import {
  createComponentWorldDeltaMap,
  transformMateDefinitionForSolve,
} from './geometry/StandardMateGeometryTransform';
import {
  isDirectionParallelWithinTolerance,
  resolveStandardMateConstraintTolerance,
  type StandardMateConstraintTolerance,
} from './StandardMateConstraintResidual';
import type {
  AssemblyMateSolverComponentState,
  StandardMateSolverInput,
} from './StandardMateSolverTypes';

/**
 * 组件在已提交配合图中保留的旋转轴。
 */
export interface StandardMateComponentRotationAxis {
  /** 本次请求解析的组件实例 ID。 */
  readonly componentInstanceId: string;
  /** 提供该自由度的配合 ID。 */
  readonly mateId: string;
  /** 该旋转轴另一侧的组件实例 ID。 */
  readonly pairedComponentInstanceId: string;
  /** 世界坐标系中的轴上一点。 */
  readonly origin: readonly [number, number, number];
  /** 世界坐标系中的单位轴向。 */
  readonly direction: readonly [number, number, number];
}

/**
 * 待排序的旋转轴候选。
 */
interface RotationAxisCandidate {
  /** 对外返回的旋转轴。 */
  readonly axis: StandardMateComponentRotationAxis;
  /** 稳定排序分数，越小优先级越高。 */
  readonly score: number;
  /** 原始 Mate 顺序，保证同分时确定性。 */
  readonly mateIndex: number;
}

/**
 * 浮动刚性组合解析结果。
 *
 * 设计说明：
 * 多个独立同轴心加轴向重合会把两个浮动组件约束成相对刚体。该结果只描述这种“相对姿态已被锁定”的局部组，
 * 不把普通单轴转动副或闭环机构误判为刚性块。
 */
export interface StandardMateFloatingRigidTransformGroup {
  /** 当前请求解析的组件实例 ID。 */
  readonly componentInstanceId: string;
  /** 同一个浮动刚性组合内的组件实例 ID。 */
  readonly componentInstanceIds: readonly string[];
  /** 支撑该刚性组合判定的 Mate ID。 */
  readonly mateIds: readonly string[];
}

/**
 * 可参与浮动刚性组合判定的同轴心 Mate。
 */
interface RigidConcentricMateCandidate {
  /** Mate 定义。 */
  readonly mate: AssemblyMateDefinition;
  /** 第一侧组件实例 ID。 */
  readonly firstComponentId: string;
  /** 第二侧组件实例 ID。 */
  readonly secondComponentId: string;
  /** 第一侧当前世界轴。 */
  readonly firstAxis: AxisGeometry;
  /** 第二侧当前世界轴。 */
  readonly secondAxis: AxisGeometry;
}

/**
 * 浮动刚性组合图中的一条组件边。
 */
export interface FloatingRigidComponentEdge {
  /** 边的一侧组件实例 ID。 */
  readonly firstComponentId: string;
  /** 边的另一侧组件实例 ID。 */
  readonly secondComponentId: string;
  /** 支撑该刚性边的 Mate ID。 */
  readonly mateIds: readonly string[];
}

/**
 * 读取 Mate 两端组件 ID。
 *
 * @param mate Mate 定义。
 * @returns 两端组件 ID。
 */
function resolveMateComponentIds(mate: AssemblyMateDefinition): readonly [string, string] {
  return [
    mate.firstEntity.componentInstanceId,
    mate.secondEntity.componentInstanceId,
  ] as const;
}

/**
 * 读取无方向组件对 key。
 *
 * @param firstComponentId 第一组件 ID。
 * @param secondComponentId 第二组件 ID。
 * @returns 可用于同组件对匹配的 key。
 */
function resolveComponentPairKey(firstComponentId: string, secondComponentId: string): string {
  return [firstComponentId, secondComponentId].sort().join('::');
}

/**
 * 判断组件是否固定。
 *
 * @param component 组件状态。
 * @returns 是否固定。
 */
function isFixedComponent(component: AssemblyMateSolverComponentState | undefined): boolean {
  return component?.fixationState === AssemblyComponentFixationState.Fixed;
}

/**
 * 判断 Mate 是否可参与自由度解析。
 *
 * @param mate Mate 定义。
 * @returns 是否可用。
 */
function canUseMateForFreedomResolve(mate: AssemblyMateDefinition): boolean {
  return !mate.suppressed
    && mate.solveStatus !== MateSolveStatus.MissingReference
    && mate.solveStatus !== MateSolveStatus.UnsupportedGeometry;
}

/**
 * 判断 Concentric 是否仍保留绕轴旋转自由度。
 *
 * @param mate Mate 定义。
 * @returns 是否可作为旋转自由度来源。
 */
function isUnlockedConcentricMate(mate: AssemblyMateDefinition): boolean {
  return canUseMateForFreedomResolve(mate)
    && mate.mateType === AssemblyMateType.Concentric
    && mate.lockRotation !== true;
}

/**
 * 判断平面 Mate 是否能与同一组件对的同轴心 Mate 形成转动副。
 *
 * @param planeMate 平面 Mate。
 * @param concentricMate 同轴心 Mate。
 * @param tolerance 几何容差。
 * @returns 是否具备转动副语义。
 */
function doesPlaneMateLockConcentricAxis(
  planeMate: AssemblyMateDefinition,
  concentricMate: AssemblyMateDefinition,
  tolerance: StandardMateConstraintTolerance,
): boolean {
  if (!canUseMateForFreedomResolve(planeMate)) {
    return false;
  }
  if (
    planeMate.mateType !== AssemblyMateType.Coincident
    && (
      planeMate.mateType !== AssemblyMateType.Distance
      || Math.abs(planeMate.dimension?.value ?? 0) > tolerance.linear
    )
  ) {
    return false;
  }

  const [planeFirstId, planeSecondId] = resolveMateComponentIds(planeMate);
  const [axisFirstId, axisSecondId] = resolveMateComponentIds(concentricMate);
  if (resolveComponentPairKey(planeFirstId, planeSecondId) !== resolveComponentPairKey(axisFirstId, axisSecondId)) {
    return false;
  }

  const firstAxis = resolveAxisGeometry(concentricMate.firstEntity);
  const secondAxis = resolveAxisGeometry(concentricMate.secondEntity);
  const firstPlane = resolvePlaneGeometry(planeMate.firstEntity);
  const secondPlane = resolvePlaneGeometry(planeMate.secondEntity);
  if (!firstAxis || !secondAxis || !firstPlane || !secondPlane) {
    return false;
  }

  return isDirectionParallelWithinTolerance(firstAxis.direction, secondAxis.direction, tolerance)
    && isDirectionParallelWithinTolerance(firstPlane.normal, firstAxis.direction, tolerance)
    && isDirectionParallelWithinTolerance(secondPlane.normal, secondAxis.direction, tolerance);
}

/**
 * 判断两条轴线当前是否已经同轴。
 *
 * @param first 第一条轴。
 * @param second 第二条轴。
 * @param tolerance 几何容差。
 * @returns 是否同轴。
 */
export function areAxesCoincident(
  first: AxisGeometry,
  second: AxisGeometry,
  tolerance: StandardMateConstraintTolerance,
): boolean {
  if (!isDirectionParallelWithinTolerance(first.direction, second.direction, tolerance)) {
    return false;
  }
  const originDelta = second.origin.clone().sub(first.origin);
  const radialDelta = originDelta.sub(first.direction.clone().multiplyScalar(originDelta.dot(first.direction)));
  return radialDelta.length() <= tolerance.linear;
}

/**
 * 判断同一组件上的两条同轴心约束轴是否具备独立约束语义。
 *
 * @param left 第一条同轴心候选。
 * @param right 第二条同轴心候选。
 * @param tolerance 几何容差。
 * @returns 两条轴能共同约束相对姿态时返回 `true`。
 */
function areRigidConcentricCandidatesIndependent(
  left: RigidConcentricMateCandidate,
  right: RigidConcentricMateCandidate,
  tolerance: StandardMateConstraintTolerance,
): boolean {
  if (!isDirectionParallelWithinTolerance(left.firstAxis.direction, right.firstAxis.direction, tolerance)) {
    return true;
  }

  const originDelta = right.firstAxis.origin.clone().sub(left.firstAxis.origin);
  const radialDelta = originDelta.sub(
    left.firstAxis.direction.clone().multiplyScalar(originDelta.dot(left.firstAxis.direction)),
  );
  return radialDelta.length() > tolerance.linear;
}

/**
 * 收集同一组件对上的可用同轴心 Mate。
 *
 * @param input 求解器输入。
 * @param currentMates 已变换到当前 world space 的 Mate 定义。
 * @param tolerance 几何容差。
 * @returns 组件对 key 到同轴心候选的映射。
 */
function collectRigidConcentricCandidatesByPair(
  input: StandardMateSolverInput,
  currentMates: readonly AssemblyMateDefinition[],
  tolerance: StandardMateConstraintTolerance,
): Map<string, RigidConcentricMateCandidate[]> {
  const candidatesByPair = new Map<string, RigidConcentricMateCandidate[]>();

  currentMates.forEach((mate) => {
    if (!isUnlockedConcentricMate(mate)) {
      return;
    }

    const firstComponentId = mate.firstEntity.componentInstanceId;
    const secondComponentId = mate.secondEntity.componentInstanceId;
    if (
      firstComponentId === secondComponentId
      || !input.components[firstComponentId]
      || !input.components[secondComponentId]
    ) {
      return;
    }

    const firstAxis = resolveAxisGeometry(mate.firstEntity);
    const secondAxis = resolveAxisGeometry(mate.secondEntity);
    if (!firstAxis || !secondAxis || !areAxesCoincident(firstAxis, secondAxis, tolerance)) {
      return;
    }

    const pairKey = resolveComponentPairKey(firstComponentId, secondComponentId);
    const candidates = candidatesByPair.get(pairKey) ?? [];
    candidates.push({
      mate,
      firstComponentId,
      secondComponentId,
      firstAxis,
      secondAxis,
    });
    candidatesByPair.set(pairKey, candidates);
  });

  return candidatesByPair;
}

/**
 * 判断同一组件对上的多条同轴心 Mate 是否已经形成刚性连接。
 *
 * @param candidates 同一组件对的同轴心候选。
 * @param currentMates 当前 world space Mate 定义。
 * @param tolerance 几何容差。
 * @returns 刚性连接成立时返回支撑该连接的 Mate ID；否则返回 `null`。
 */
function resolveFloatingRigidEdgeMateIds(
  candidates: readonly RigidConcentricMateCandidate[],
  currentMates: readonly AssemblyMateDefinition[],
  tolerance: StandardMateConstraintTolerance,
): readonly string[] | null {
  if (candidates.length < 2) {
    return null;
  }

  let hasIndependentConcentricPair = false;
  for (let leftIndex = 0; leftIndex < candidates.length; leftIndex += 1) {
    for (let rightIndex = leftIndex + 1; rightIndex < candidates.length; rightIndex += 1) {
      if (areRigidConcentricCandidatesIndependent(candidates[leftIndex], candidates[rightIndex], tolerance)) {
        hasIndependentConcentricPair = true;
        break;
      }
    }
    if (hasIndependentConcentricPair) {
      break;
    }
  }
  if (!hasIndependentConcentricPair) {
    return null;
  }

  const mateIds = new Set<string>(candidates.map((candidate) => candidate.mate.mateId));
  const hasAxialPlaneLock = candidates.some((candidate) => (
    currentMates.some((mate) => {
      if (mate.mateId === candidate.mate.mateId) {
        return false;
      }
      const doesLock = doesPlaneMateLockConcentricAxis(mate, candidate.mate, tolerance);
      if (doesLock) {
        mateIds.add(mate.mateId);
      }
      return doesLock;
    })
  ));

  return hasAxialPlaneLock ? [...mateIds] : null;
}

/**
 * 从 Mate 图中解析浮动刚性连接边。
 *
 * @param input 求解器输入。
 * @param currentMates 已变换到当前 world space 的 Mate 定义。
 * @param tolerance 几何容差。
 * @returns 浮动刚性连接边集合。
 */
export function resolveFloatingRigidComponentEdges(
  input: StandardMateSolverInput,
  currentMates: readonly AssemblyMateDefinition[],
  tolerance: StandardMateConstraintTolerance,
): FloatingRigidComponentEdge[] {
  const candidatesByPair = collectRigidConcentricCandidatesByPair(input, currentMates, tolerance);
  const edges: FloatingRigidComponentEdge[] = [];

  candidatesByPair.forEach((candidates) => {
    const mateIds = resolveFloatingRigidEdgeMateIds(candidates, currentMates, tolerance);
    if (!mateIds) {
      return;
    }

    edges.push({
      firstComponentId: candidates[0].firstComponentId,
      secondComponentId: candidates[0].secondComponentId,
      mateIds,
    });
  });

  return edges;
}

/**
 * 在刚性连接边图中解析目标组件所在的全浮动刚性组合。
 *
 * @param input 求解器输入。
 * @param componentInstanceId 目标组件实例 ID。
 * @param edges 刚性连接边。
 * @returns 全浮动刚性组合；不存在时返回 `null`。
 */
function resolveFloatingRigidTransformGroupFromEdges(
  input: StandardMateSolverInput,
  componentInstanceId: string,
  edges: readonly FloatingRigidComponentEdge[],
): StandardMateFloatingRigidTransformGroup | null {
  const component = input.components[componentInstanceId];
  if (!component || isFixedComponent(component)) {
    return null;
  }

  const edgesByComponentId = new Map<string, FloatingRigidComponentEdge[]>();
  edges.forEach((edge) => {
    const firstEdges = edgesByComponentId.get(edge.firstComponentId) ?? [];
    firstEdges.push(edge);
    edgesByComponentId.set(edge.firstComponentId, firstEdges);

    const secondEdges = edgesByComponentId.get(edge.secondComponentId) ?? [];
    secondEdges.push(edge);
    edgesByComponentId.set(edge.secondComponentId, secondEdges);
  });
  if (!edgesByComponentId.has(componentInstanceId)) {
    return null;
  }

  const componentIds = new Set<string>();
  const mateIds = new Set<string>();
  const queue = [componentInstanceId];
  for (let queueIndex = 0; queueIndex < queue.length; queueIndex += 1) {
    const currentComponentId = queue[queueIndex];
    if (componentIds.has(currentComponentId)) {
      continue;
    }
    componentIds.add(currentComponentId);

    const currentComponent = input.components[currentComponentId];
    if (!currentComponent || isFixedComponent(currentComponent)) {
      return null;
    }

    const connectedEdges = edgesByComponentId.get(currentComponentId) ?? [];
    connectedEdges.forEach((edge) => {
      edge.mateIds.forEach((mateId) => {
        mateIds.add(mateId);
      });
      const nextComponentId = edge.firstComponentId === currentComponentId
        ? edge.secondComponentId
        : edge.firstComponentId;
      if (!componentIds.has(nextComponentId)) {
        queue.push(nextComponentId);
      }
    });
  }

  return componentIds.size > 1
    ? {
        componentInstanceId,
        componentInstanceIds: [...componentIds],
        mateIds: [...mateIds],
      }
    : null;
}

/**
 * 从当前 Mate 定义中解析目标组件所在的全浮动刚性组合。
 *
 * @param input 求解器输入。
 * @param currentMates 已变换到当前 world space 的 Mate 定义。
 * @param componentInstanceId 目标组件实例 ID。
 * @param tolerance 几何容差。
 * @returns 全浮动刚性组合；不存在时返回 `null`。
 */
function resolveFloatingRigidTransformGroupFromCurrentMates(
  input: StandardMateSolverInput,
  currentMates: readonly AssemblyMateDefinition[],
  componentInstanceId: string,
  tolerance: StandardMateConstraintTolerance,
): StandardMateFloatingRigidTransformGroup | null {
  const edges = resolveFloatingRigidComponentEdges(input, currentMates, tolerance);
  return resolveFloatingRigidTransformGroupFromEdges(input, componentInstanceId, edges);
}

/**
 * 读取目标组件（或其所在刚性组）在 Concentric Mate 中的自身侧轴线与对侧轴线。
 *
 * 设计说明：
 * `selfComponentIds` 表示旋转时被视为“同一刚体”的组件集合：普通零件只含自身；被多重配合约束成刚性组的成员
 * 则含整组成员。只有“恰好一端在该集合内”的同轴心才提供旋转自由度——两端都在集合内是刚体内部约束（不提供
 * 相对转动），两端都在集合外与当前组件无关。这样刚性组也能沿它对外部零件的转动副轴线整体旋转。
 *
 * @param mate Mate 定义。
 * @param selfComponentIds 旋转时视为同一刚体的组件集合。
 * @returns 两侧轴线信息；非外部同轴心时返回 `null`。
 */
function resolveComponentConcentricAxes(
  mate: AssemblyMateDefinition,
  selfComponentIds: ReadonlySet<string>,
): {
  readonly componentAxis: AxisGeometry;
  readonly pairedAxis: AxisGeometry;
  readonly pairedComponentInstanceId: string;
} | null {
  const firstAxis = resolveAxisGeometry(mate.firstEntity);
  const secondAxis = resolveAxisGeometry(mate.secondEntity);
  if (!firstAxis || !secondAxis) {
    return null;
  }
  const firstInSelf = selfComponentIds.has(mate.firstEntity.componentInstanceId);
  const secondInSelf = selfComponentIds.has(mate.secondEntity.componentInstanceId);
  // 只保留恰好一端在刚体内的外部同轴心。
  if (firstInSelf === secondInSelf) {
    return null;
  }
  if (firstInSelf) {
    return {
      componentAxis: firstAxis,
      pairedAxis: secondAxis,
      pairedComponentInstanceId: mate.secondEntity.componentInstanceId,
    };
  }
  return {
    componentAxis: secondAxis,
    pairedAxis: firstAxis,
    pairedComponentInstanceId: mate.firstEntity.componentInstanceId,
  };
}

/**
 * 计算旋转轴候选优先级。
 *
 * @param input 求解器输入。
 * @param pairedComponentInstanceId 对侧组件 ID。
 * @param hasPlanarLock 是否存在同组件对平面锁定。
 * @returns 排序分数。
 */
function calculateRotationAxisCandidateScore(
  input: StandardMateSolverInput,
  pairedComponentInstanceId: string,
  hasPlanarLock: boolean,
): number {
  if (isFixedComponent(input.components[pairedComponentInstanceId])) {
    return hasPlanarLock ? 0 : 1;
  }
  return hasPlanarLock ? 2 : 3;
}

/**
 * 将求解器输入中的 Mate 几何统一变换到当前 world space。
 *
 * @param input 求解器输入。
 * @returns 当前求解步可使用的 Mate 定义。
 */
function resolveCurrentMateDefinitions(input: StandardMateSolverInput): readonly AssemblyMateDefinition[] {
  const componentDeltaMap = createComponentWorldDeltaMap(input.components);
  return input.mateDefinitions.map((mate) => (
    transformMateDefinitionForSolve(mate, componentDeltaMap, input.components)
  ));
}

/**
 * 从已提交 Mate 图中解析目标组件所在的全浮动刚性组合。
 *
 * 设计说明：
 * 该函数只返回“全浮动”的刚性组合。只要组合内出现 fixed 组件，外层命令就不应把它作为可共同写入 transform 的对象组，
 * 否则会把固定组件错误带走。
 *
 * @param input 标准配合求解器输入。
 * @param componentInstanceId 需要解析的组件实例 ID。
 * @returns 全浮动刚性组合；不存在时返回 `null`。
 */
export function resolveStandardMateFloatingRigidTransformGroup(
  input: StandardMateSolverInput,
  componentInstanceId: string,
): StandardMateFloatingRigidTransformGroup | null {
  const component = input.components[componentInstanceId];
  if (!component || isFixedComponent(component)) {
    return null;
  }

  const tolerance = resolveStandardMateConstraintTolerance({
    epsilon: input.epsilon,
    angleEpsilon: input.angleEpsilon,
  });
  const currentMates = resolveCurrentMateDefinitions(input);
  return resolveFloatingRigidTransformGroupFromCurrentMates(
    input,
    currentMates,
    componentInstanceId,
    tolerance,
  );
}

/**
 * 从配合图解析组件可用于交互旋转的轴线。
 *
 * 设计说明：
 * “旋转零部件”不能把带配合组件当成自由刚体绕自身原点改 quaternion。若组件由 Concentric
 * 保留了绕公共轴的自由度，本函数把这个自由度提升为命令层可消费的 plain-data 轴线，后续仍由
 * 标准配合求解器统一处理联动组件与闭环残差。
 *
 * @param input 标准配合求解器输入。
 * @param componentInstanceId 需要解析剩余自由度的组件 ID。
 * @returns 可用旋转轴；不存在明确轴线自由度时返回 null。
 */
export function resolveStandardMateComponentRotationAxis(
  input: StandardMateSolverInput,
  componentInstanceId: string,
): StandardMateComponentRotationAxis | null {
  const component = input.components[componentInstanceId];
  if (!component || isFixedComponent(component)) {
    return null;
  }

  const tolerance = resolveStandardMateConstraintTolerance({
    epsilon: input.epsilon,
    angleEpsilon: input.angleEpsilon,
  });
  const currentMates = resolveCurrentMateDefinitions(input);
  /**
   * 组件若被多重配合约束成刚性组，旋转时整组作为单一刚体。此时不再直接判为不可旋转，而是把“自身”扩展为整组成员：
   * 只要该刚性组对某个外部零件仍保留转动副（外部同轴心），整组就能沿该轴线一起旋转（如电机-连杆刚性块绕电机转动副
   * 轴线转动）；若刚性组只有内部约束、没有任何对外转动副，则没有明确轴线自由度，返回 null 交给自由旋转绕自身中心。
   */
  const rigidGroup = resolveFloatingRigidTransformGroupFromCurrentMates(
    input,
    currentMates,
    componentInstanceId,
    tolerance,
  );
  const selfComponentIds = new Set<string>(
    rigidGroup ? rigidGroup.componentInstanceIds : [componentInstanceId],
  );
  const candidates: RotationAxisCandidate[] = [];

  currentMates.forEach((mate, mateIndex) => {
    if (!isUnlockedConcentricMate(mate)) {
      return;
    }
    const axes = resolveComponentConcentricAxes(mate, selfComponentIds);
    if (!axes || !areAxesCoincident(axes.componentAxis, axes.pairedAxis, tolerance)) {
      return;
    }

    const hasPlanarLock = currentMates.some((candidate) => (
      candidate.mateId !== mate.mateId
      && doesPlaneMateLockConcentricAxis(candidate, mate, tolerance)
    ));
    const sourceAxis = isFixedComponent(input.components[axes.pairedComponentInstanceId])
      ? axes.pairedAxis
      : axes.componentAxis;
    const direction = normalizeVector(sourceAxis.direction, tolerance.linear);
    if (!direction) {
      return;
    }

    candidates.push({
      mateIndex,
      score: calculateRotationAxisCandidateScore(input, axes.pairedComponentInstanceId, hasPlanarLock),
      axis: {
        componentInstanceId,
        mateId: mate.mateId,
        pairedComponentInstanceId: axes.pairedComponentInstanceId,
        origin: tupleFromVector(sourceAxis.origin),
        direction: tupleFromVector(direction),
      },
    });
  });

  candidates.sort((left, right) => (
    left.score - right.score || left.mateIndex - right.mateIndex
  ));
  return candidates[0]?.axis ?? null;
}
